import axios from "axios";
import { environment } from "./config/environment";
import { auth0Config } from "./config/Auth0Config";
import { auth0Client } from "./providers/AuthContext";
import { handleAuthError } from "./utils/authErrorHandler";

// Ensure API base URL uses HTTPS in production
const getSecureBaseURL = () => {
  const baseURL = environment.apiBaseUrl;
  if (environment.isProduction && baseURL && baseURL.startsWith("http://")) {
    return baseURL.replace("http://", "https://");
  }
  return baseURL;
};

const instance = axios.create({
  baseURL: getSecureBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
  // Add CORS settings to match fetch behavior
  withCredentials: environment.security.cors.allowCredentials,
});

// Add a request interceptor to include the access token
instance.interceptors.request.use(
  async (config) => {
    try {
      // Get token directly from Auth0 client
      const token = await auth0Client.getTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        },
      });

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.error("No authentication token available");
        throw new Error("No authentication token available");
      }
    } catch (error: any) {
      console.error("Error acquiring token:", error);

      // If token acquisition fails due to auth issues, handle auth error
      if (
        error.error === "login_required" ||
        error.error === "consent_required" ||
        error.error === "interaction_required" ||
        error.error === "missing_refresh_token" ||
        error.message?.includes("Missing Refresh Token")
      ) {
        handleAuthError(error);
        return Promise.reject(new Error("Authentication error"));
      }

      throw error;
    }
    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.error("Authentication failed - redirecting to login");

      // Try to get more specific error information
      const errorData = error.response?.data;
      if (errorData && typeof errorData === "object") {
        console.error("401 Error details:", errorData);
      }

      // Handle the auth error
      handleAuthError(error);
    } else if (error.response?.status === 403) {
      console.error(
        "Access forbidden - user may not have required permissions"
      );
    } else if (error.response) {
      console.error("API Error:", error.response.status, error.response.data);
    } else if (error.request) {
      console.error("Network error:", error.request);
    } else {
      console.error("Error:", error.message);
    }
    return Promise.reject(error);
  }
);

export default instance;
